<template>
    <div class="chart-wrapper">
        <v-chart class="chart" :option="chartOptions" renderer="canvas" autoresize @datazoom="onDataZoom" />
    </div>
</template>

<script>
import { LineChart } from 'echarts/charts'
import {
    DataZoomComponent,
    DataZoomInsideComponent,
    GridComponent,
    LegendComponent,
    TooltipComponent
} from 'echarts/components'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import VChart, { THEME_KEY } from 'vue-echarts'

import { debounce } from '../../../../utils/eventHandling.js'

export default {
    name: 'CpuChart',
    components: {
        VChart
    },
    provide: {
        [THEME_KEY]: 'light'
    },
    props: {
        resources: {
            type: Object,
            required: true
        },
        instance: {
            type: Object,
            required: true

        }
    },
    setup () {
        use([
            CanvasRenderer,
            DataZoomComponent,
            DataZoomInsideComponent,
            LineChart,
            TooltipComponent,
            LegendComponent,
            GridComponent
        ])
    },
    data () {
        return {
            zoom: {
                start: 80,
                end: 100
            }
        }
    },
    computed: {
        chartOptions () {
            return {
                tooltip: {
                    trigger: 'axis',
                    formatter: function (params) {
                        const timestamp = Number(params[0].axisValue)
                        const date = new Date(timestamp)
                        const formattedDate = date.toLocaleString()

                        let content = `${formattedDate}<br/>`
                        params.forEach(item => {
                            content += `${item.seriesName}: ${item.data.toFixed(2)}%<br/>`
                        })
                        return content
                    }
                },
                grid: {
                    left: '0%',
                    right: '0%',
                    containLabel: true
                },
                xAxis: this.xAxis,
                yAxis: this.yAxis,
                series: this.series,
                dataZoom: [
                    {
                        type: 'slider', // visible scrollbar
                        show: true,
                        xAxisIndex: 0,
                        // starting window (e.g., show last 20% if number of entries is over 100)
                        start: this.filteredResources.length < 100 ? 0 : this.zoom.start,
                        end: this.zoom.end,
                        labelFormatter: (value) => {
                            const ts = this.filteredResources[value]?.ts ?? 0
                            const date = new Date(Number(ts))
                            return `{label|${date.toLocaleDateString()} ${date.toLocaleTimeString()}}`
                        },
                        moveHandleSize: 12,
                        textStyle: {
                            color: '#333',
                            fontSize: 12,
                            rich: {
                                label: {
                                    borderColor: 'grey',
                                    backgroundColor: 'white',
                                    borderWidth: 1,
                                    borderRadius: 4,
                                    padding: [4, 6]
                                }
                            }
                        }
                    },
                    {
                        type: 'inside', // scroll with mouse or touch
                        xAxisIndex: 0,
                        start: 80,
                        end: 100
                    }
                ]
            }
        },
        series () {
            return [
                {
                    name: 'CPU',
                    type: 'line',
                    stack: 'Total',
                    data: this.filteredResources.map(res => {
                        // first responses might not contain relevant info
                        const cpu = res.cpu ?? 0

                        if (this.instance.stack?.properties?.cpu) {
                            // scaling down to match stack cpu allocation
                            return this.capGraph((cpu / this.instance.stack.properties.cpu) * 100)
                        }

                        return this.capGraph(cpu)
                    })
                }
            ]
        },
        xAxis () {
            return {
                type: 'category',
                boundaryGap: false,
                data: this.filteredResources.map(res => res.ts),
                axisLabel: {
                    formatter: function (value) {
                        const date = new Date(Number(value))
                        return date.toLocaleTimeString()
                    }
                }
            }
        },
        yAxis () {
            return [{
                type: 'value',
                position: 'right',
                axisLabel: {
                    formatter: function (value) {
                        return `${value}%`
                    }
                }
            }]
        },
        filteredResources () {
            return this.resources.filter(res => res.cpu && res.ts)
        }
    },
    methods: {
        onDataZoom: debounce(function (event) {
            const batch = event?.batch?.[0]

            if (batch?.type === 'datazoom') {
                this.zoom.start = batch.start
                this.zoom.end = batch.end
            }
        }, 50),
        capGraph (val) {
            switch (true) {
            case val <= 0:
                return 0
            case val >= 150:
                return 150
            default:
                return val
            }
        }
    }
}
</script>

<style scoped lang="scss">
.chart-wrapper {
    flex: 1;
    display: flex;

    .chart {
        flex: 1;
        height: 100%;
        width: 100%;
        min-height: 250px;
    }
}
</style>
